/* eslint-disable @typescript-eslint/no-explicit-any */
import { ArrowDown, ArrowUp, Eye, EyeOff, RefreshCw } from "lucide-react";
import { useEffect, useState } from "react";

import { Select } from "../ds";
import { ColorRow, Row, Section, SegRow, SliderRow, ToggleRow, getIn } from "./FormatControls";
import type { SWidget } from "./model";

/* Format-pane sections for matrix widgets (display.matrix.*) and the extra
 * table layout options (display.tablePresentation.*). Every control writes
 * exactly the key named beside it; nothing is written until touched, and an
 * unset key renders as before. Kept in its own file so FormatPane.tsx only
 * needs a one-line mount per group. */

type SetFn = (path: string[], value: any, live?: boolean) => void;
type SetManyFn = (pairs: Array<[string[], any]>, live?: boolean) => void;

const FONT_OPTIONS = (
  <>
    <option value="default">Report font (default)</option>
    <option value="condensed">Condensed</option>
    <option value="serif">Serif</option>
    <option value="mono">Monospace</option>
  </>
);

function colName(m: any): string {
  return typeof m === "string" ? m : m?.name ?? m?.key ?? String(m);
}

/** Distinct column headings of a matrix as currently drawn in the preview.
 * The designer canvas is a same-origin iframe, so its DOM can be read; when
 * it cannot (not mounted yet / blocked) the list is simply empty. */
export function readMatrixColumns(widgetId: string): string[] {
  const out: string[] = [];
  try {
    const sel = `[data-widget-id="${CSS.escape(widgetId)}"] th`;
    document.querySelectorAll("iframe").forEach((frame) => {
      const doc = (frame as HTMLIFrameElement).contentDocument;
      doc?.querySelectorAll(sel).forEach((th) => {
        const cls = (th as HTMLElement).className || "";
        if (!/rpt-matrix-(col-header|group-header)/.test(cls) || /rpt-matrix-total-col/.test(cls)) return;
        const text = th.textContent?.trim();
        if (text && !out.includes(text)) out.push(text);
      });
    });
  } catch {
    /* cross-origin or detached: no preview columns */
  }
  return out;
}

function WidthRow({ label, value, onChange }: { label: string; value?: string; onChange: (v: string | undefined) => void }) {
  const match = /^([\d.]+)(%|px)$/.exec(value ?? "");
  const [unit, setUnit] = useState(match?.[2] ?? "%");
  useEffect(() => { if (match) setUnit(match[2]); }, [value]); // eslint-disable-line react-hooks/exhaustive-deps
  return <Row label={label} hint="Blank means automatic. % scales with the matrix; px is a fixed width on the report canvas.">
    <input aria-label={`${label} amount`} className="ds-input ds-input--sm" type="number" min={1} max={unit === "%" ? 100 : 2000} placeholder="Auto" value={match?.[1] ?? ""} style={{ width: 75 }} onChange={(e) => { const n = Number(e.target.value); onChange(e.target.value && n > 0 ? `${Math.min(unit === "%" ? 100 : 2000, n)}${unit}` : undefined); }} />
    <Select aria-label={`${label} unit`} inputSize="sm" value={unit} onChange={(e) => { const u = e.target.value; setUnit(u); if (match) onChange(`${Math.min(u === "%" ? 100 : 2000, Number(match[1]))}${u}`); }}><option value="%">%</option><option value="px">px</option></Select>
  </Row>;
}

export function MatrixFormatSections({ widget, set, setMany, d }: { widget: SWidget; set: SetFn; setMany: SetManyFn; d: any }) {
  const m = (k: string) => getIn(d, ["matrix", k]);
  const mset = (k: string, v: any, live = false) => set(["matrix", k], v, live);
  const dimensions: string[] = widget.query?.dimensions ?? [];
  const hierarchy: string[] = m("rowFields") ?? [m("rowField"), ...dimensions.filter((f) => f !== m("rowField") && f !== m("columnField") && f !== m("subColumnField"))].filter(Boolean);
  const saveHierarchy = (fields: string[]) => setMany([[["matrix", "rowFields"], fields], [["matrix", "rowField"], fields[0]]]);
  const moveHierarchy = (i: number, delta: number) => { const next = [...hierarchy]; [next[i], next[i + delta]] = [next[i + delta], next[i]]; saveHierarchy(next); };
  const dense = !!m("subColumnField");
  const baseSize = dense ? 8 : 13;
  const fit = (m("autoFit") ?? "fill") as "fill" | "content" | "equal";
  const widths: Record<string, string> = m("columnWidths") ?? {};

  const [seen, setSeen] = useState<string[]>(() => readMatrixColumns(widget.id));
  useEffect(() => {
    const timer = window.setTimeout(() => setSeen(readMatrixColumns(widget.id)), 600);
    return () => window.clearTimeout(timer);
  }, [widget.id, widget.query, m("columnField"), m("subColumnField")]); // eslint-disable-line react-hooks/exhaustive-deps
  const known = [...seen];
  Object.keys(widths).forEach((k) => known.includes(k) || known.push(k));

  const sizeKeys = ["valueFontSize", "headerFontSize", "fontFamily", "valueFontWeight", "valueColor", "valueAlign", "columnHeaderAlign"];
  const firstKeys = ["rowLabelAlign", "rowLabelWidth", "rowLabelFontSize", "wrapRowLabels"];
  const fitKeys = ["autoFit", "shrinkToFit", "wrapHeaders", "rowLabelWidth", "columnWidths"];
  const lookKeys = ["zebra", "zebraColor", "gridlines", "gridColor", "rowPadding", "stickyHeader", "nullDisplay", "headerBackground", "headerColor"];
  const totalKeys = ["totalRow", "totalRowLabel", "totalRowFontWeight", "totalRowBackground", "showColumnTotals", "totalColumnLabel", "totalsAdditive"];
  const anySet = (keys: string[]) => keys.some((k) => m(k) !== undefined);
  const reset = (keys: string[]) => setMany(keys.map((k) => [["matrix", k], undefined] as [string[], any]));

  return (
    <>
      <Section id="mxfirst" title="First column" defaultOpen summary="Labels, alignment and width" canReset={anySet(firstKeys)} onReset={() => reset(firstKeys)}>
        <div className="ds-small ds-muted">Select the matrix, then edit the item names here. These settings do not change the numbers.</div>
        <SegRow label="Text alignment" value={(m("rowLabelAlign") ?? "default") as string} onChange={(v) => mset("rowLabelAlign", v === "default" ? undefined : v)} options={[{ value: "default", label: "Auto" }, { value: "left", label: "Left" }, { value: "center", label: "Centre" }, { value: "right", label: "Right" }]} />
        <WidthRow label="First column width" value={m("rowLabelWidth")} onChange={(v) => setMany([[["matrix", "rowLabelWidth"], v], [["matrix", "autoFit"], "fill"]])} />
        <SliderRow label="Text size" value={m("rowLabelFontSize")} defaultValue={baseSize} min={8} max={64} unit="px" onChange={(v) => mset("rowLabelFontSize", v, true)} />
        <ToggleRow label="Wrap long labels" checked={!!m("wrapRowLabels")} onChange={(v) => mset("wrapRowLabels", v)} />
        <details className="ds-small"><summary>How to format this matrix</summary><p>1. Choose Left, Centre or Right for the first column. 2. Enter its width (start with 22%). 3. Turn on wrapping for long item names. Use Matrix text for numbers and column headers, and Fit columns for the other widths. Clear a width to return to Auto. Changes are saved to the draft.</p></details>
      </Section>
      <Section id="mxhierarchy" title="Row hierarchy" defaultOpen summary="Expand rows with +">
        <div className="ds-small ds-muted">Rows are nested in this order. Use + beside a region to show its projects. Column fields stay on the horizontal axis.</div>
        {hierarchy.map((field, i) => <Row key={field} label={field}>
          <button type="button" aria-label={`Move ${field} up`} disabled={i === 0} onClick={() => moveHierarchy(i, -1)}><ArrowUp size={14} /></button>
          <button type="button" aria-label={`Move ${field} down`} disabled={i === hierarchy.length - 1} onClick={() => moveHierarchy(i, 1)}><ArrowDown size={14} /></button>
          <button type="button" aria-label={`Remove ${field} from hierarchy`} disabled={hierarchy.length === 1} onClick={() => saveHierarchy(hierarchy.filter((_, n) => n !== i))}>Remove</button>
        </Row>)}
        <Row label="Add row level"><Select inputSize="sm" value="" onChange={(e) => e.target.value && saveHierarchy([...hierarchy, e.target.value])}>
          <option value="">Choose a field</option>
          {dimensions.filter((f) => !hierarchy.includes(f) && f !== m("columnField") && f !== m("subColumnField")).map((f) => <option key={f} value={f}>{f}</option>)}
        </Select></Row>
      </Section>
      <Section id="mxtext" title="Matrix text" defaultOpen summary={m("valueFontSize") ? `${m("valueFontSize")} px values` : "Default sizes"} canReset={anySet(sizeKeys)} onReset={() => reset(sizeKeys)}>
        <SliderRow label="Values" hint="The numbers in the cells." value={m("valueFontSize")} defaultValue={baseSize} min={8} max={64} unit="px" onChange={(v) => mset("valueFontSize", v, true)} />
        <SliderRow label="Column headers" value={m("headerFontSize")} defaultValue={baseSize} min={8} max={64} unit="px" onChange={(v) => mset("headerFontSize", v, true)} />
        <Row label="Font">
          <Select inputSize="sm" value={(m("fontFamily") as string) ?? "default"} onChange={(e) => mset("fontFamily", e.target.value === "default" ? undefined : e.target.value)}>
            {FONT_OPTIONS}
          </Select>
        </Row>
        <SegRow label="Value weight" value={(m("valueFontWeight") ?? "normal") as string} onChange={(v) => mset("valueFontWeight", v === "normal" ? undefined : v)} options={[{ value: "normal", label: "Normal" }, { value: "semibold", label: "Semi" }, { value: "bold", label: "Bold" }]} />
        <ColorRow label="Value colour" value={m("valueColor")} onChange={(v) => mset("valueColor", v, true)} />
        <SegRow label="Value alignment" value={(m("valueAlign") ?? "default") as string} onChange={(v) => mset("valueAlign", v === "default" ? undefined : v)} options={[{ value: "default", label: "Auto" }, { value: "left", label: "Left" }, { value: "center", label: "Centre" }, { value: "right", label: "Right" }]} />
        <SegRow label="Header alignment" hint="Only the headings above the numeric columns." value={(m("columnHeaderAlign") ?? "default") as string} onChange={(v) => mset("columnHeaderAlign", v === "default" ? undefined : v)} options={[{ value: "default", label: "Auto" }, { value: "left", label: "Left" }, { value: "center", label: "Centre" }, { value: "right", label: "Right" }]} />
      </Section>

      <Section id="mxfit" title="Fit columns" defaultOpen summary={fit === "fill" ? "Fill the card" : fit === "content" ? "To content" : "Equal widths"} canReset={anySet(fitKeys)} onReset={() => reset(fitKeys)}>
        <SegRow
          label="Column sizing"
          hint="Content: each column is as wide as its numbers. Equal: all value columns share the width."
          value={fit}
          onChange={(v) => mset("autoFit", v === "fill" ? undefined : v)}
          options={[{ value: "fill", label: "Fill card" }, { value: "content", label: "To content" }, { value: "equal", label: "Equal" }]}
        />
        {fit !== "fill" ? <ToggleRow label="Shrink text to fit" hint="Smaller text (never under 10 px) when the columns would not fit the card." checked={!!m("shrinkToFit")} onChange={(v) => mset("shrinkToFit", v || undefined)} /> : null}
        <ToggleRow label="Wrap column headers" checked={!!m("wrapHeaders")} onChange={(v) => mset("wrapHeaders", v || undefined)} />
        <div className="st-fsub">Column widths</div>
        {known.length === 0 ? (
          <div className="ds-small ds-faint" style={{ marginBottom: 6 }}>The column headings appear here once the preview has loaded the data.</div>
        ) : (
          known.map((c) => (
            <WidthRow
              key={c}
              label={c}
              value={widths[c]}
              onChange={(v) => setMany([[["matrix", "columnWidths", c], v], [["matrix", "autoFit"], "fill"]])}
            />
          ))
        )}
        <div className="st-frow-pair" style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 6 }}>
          <button type="button" className="st-arr__seg" onClick={() => setSeen(readMatrixColumns(widget.id))} title="Read the column headings from the preview"><RefreshCw size={12} /> Read columns</button>
          <button type="button" className="st-arr__seg" disabled={!Object.keys(widths).length} onClick={() => mset("columnWidths", undefined)}>Auto widths</button>
        </div>
      </Section>

      <Section id="mxlook" title="Matrix look" summary={m("zebra") ? "Striped" : "From the Look"} canReset={anySet(lookKeys)} onReset={() => reset(lookKeys)}>
        <ColorRow label="Header background" value={m("headerBackground")} onChange={(v) => mset("headerBackground", v, true)} />
        <ColorRow label="Header text" value={m("headerColor")} onChange={(v) => mset("headerColor", v, true)} />
        <ToggleRow label="Zebra stripes" hint="Shade every other row." checked={!!m("zebra")} onChange={(v) => mset("zebra", v || undefined)} />
        {m("zebra") ? <ColorRow label="Stripe colour" value={m("zebraColor")} onChange={(v) => mset("zebraColor", v, true)} /> : null}
        <SegRow label="Gridlines" value={(m("gridlines") ?? "default") as string} onChange={(v) => mset("gridlines", v === "default" ? undefined : v)} options={[{ value: "default", label: "Look" }, { value: "none", label: "None" }, { value: "horizontal", label: "Rows" }, { value: "vertical", label: "Cols" }, { value: "both", label: "Both" }]} />
        {m("gridlines") && m("gridlines") !== "none" ? <ColorRow label="Gridline colour" value={m("gridColor")} onChange={(v) => mset("gridColor", v, true)} /> : null}
        <SegRow label="Row spacing" value={(m("rowPadding") ?? "normal") as string} onChange={(v) => mset("rowPadding", v === "normal" ? undefined : v)} options={[{ value: "compact", label: "Compact" }, { value: "normal", label: "Normal" }, { value: "roomy", label: "Roomy" }]} />
        <ToggleRow label="Sticky header" hint="The header stays visible while the matrix scrolls." checked={!!m("stickyHeader")} onChange={(v) => mset("stickyHeader", v || undefined)} />
        <Row label="Empty cells show" hint="Default is a dash.">
          <input className="ds-input ds-input--sm" maxLength={12} placeholder="—" defaultValue={(m("nullDisplay") as string) ?? ""} key={`nd-${m("nullDisplay") ?? ""}`} onBlur={(e) => mset("nullDisplay", e.target.value === "" ? undefined : e.target.value)} />
        </Row>
      </Section>

      <Section id="mxtotals" title="Matrix totals" summary={m("totalRow") || m("showColumnTotals") ? [m("totalRow") ? "Row" : "", m("showColumnTotals") ? "Column" : ""].filter(Boolean).join(" + ") : "Off"} canReset={anySet(totalKeys)} onReset={() => reset(totalKeys)}>
        <ToggleRow label="Total row (bottom)" checked={!!(m("totalRow") || m("showRowTotals"))} onChange={(v) => setMany([[["matrix", "totalRow"], v || undefined], [["matrix", "showRowTotals"], undefined]])} />
        {m("totalRow") || m("showRowTotals") ? (
          <>
            <Row label="Row label">
              <input className="ds-input ds-input--sm" placeholder="Total" defaultValue={(m("totalRowLabel") as string) ?? ""} key={`trl-${m("totalRowLabel") ?? ""}`} onBlur={(e) => mset("totalRowLabel", e.target.value.trim() || undefined)} />
            </Row>
            <SegRow label="Row weight" value={(m("totalRowFontWeight") ?? "bold") as string} onChange={(v) => mset("totalRowFontWeight", v === "bold" ? undefined : v)} options={[{ value: "normal", label: "Normal" }, { value: "semibold", label: "Semi" }, { value: "bold", label: "Bold" }]} />
            <ColorRow label="Row background" value={m("totalRowBackground")} onChange={(v) => mset("totalRowBackground", v, true)} />
          </>
        ) : null}
        <ToggleRow label="Total column (right)" hint="Adds up each row. Not drawn for percentages, ratios or averages (a sum of ratios is wrong)." checked={!!m("showColumnTotals")} onChange={(v) => mset("showColumnTotals", v || undefined)} />
        {m("showColumnTotals") ? (
          <>
            <Row label="Column label">
              <input className="ds-input ds-input--sm" maxLength={12} placeholder="Total" defaultValue={(m("totalColumnLabel") as string) ?? ""} key={`tcl-${m("totalColumnLabel") ?? ""}`} onBlur={(e) => mset("totalColumnLabel", e.target.value.trim() || undefined)} />
            </Row>
            <ToggleRow label="Always allow" hint="Draw the Total column even if the measure looks like a ratio." checked={m("totalsAdditive") === true} onChange={(v) => mset("totalsAdditive", v ? true : undefined)} />
          </>
        ) : null}
      </Section>
    </>
  );
}

/* ------------------------------------------------------------------ */
/* Table layout: fit, sticky header, gridlines, row numbers/height,     */
/* default sort, hide/reorder columns, per-column number format.        */
/* ------------------------------------------------------------------ */

const FORMAT_PRESETS: Array<{ value: string; label: string; nf: any }> = [
  { value: "auto", label: "Auto", nf: undefined },
  { value: "integer", label: "Whole number (1,234)", nf: { type: "number", decimals: 0 } },
  { value: "dec2", label: "2 decimals (1,234.50)", nf: { type: "number", decimals: 2 } },
  { value: "k", label: "Thousands (1.2K)", nf: { type: "number", units: "thousands", decimals: 1 } },
  { value: "m", label: "Millions (1.2M)", nf: { type: "number", units: "millions", decimals: 1 } },
  { value: "usd", label: "US dollars ($1,234)", nf: { type: "currency", currency: "USD", decimals: 0 } },
  { value: "usdc", label: "US dollars compact ($1.2M)", nf: { type: "currency", currency: "USD", units: "auto", decimals: 1 } },
  { value: "pct", label: "Percent (12.5%)", nf: { type: "percent", decimals: 1 } },
];

function presetOf(nf: any): string {
  if (!nf) return "auto";
  const hit = FORMAT_PRESETS.find((p) => p.nf && JSON.stringify(p.nf) === JSON.stringify(nf));
  return hit ? hit.value : "custom";
}

export function TableLayoutSection({ widget, set, setMany, d }: { widget: SWidget; set: SetFn; setMany: SetManyFn; d: any }) {
  const tp = d.tablePresentation ?? {};
  const isTable = widget.type === "table";
  const cols: string[] = [...(widget.query?.dimensions ?? []), ...(widget.query?.measures ?? [])].map(colName);
  const hidden: string[] = tp.hiddenColumns ?? [];
  const order: string[] = tp.columnOrder ?? [];
  const fit = (tp.autoFit ?? "fill") as "fill" | "content" | "equal";
  const formats: Record<string, any> = tp.columnNumberFormats ?? {};
  const tset = (k: string, v: any, live = false) => set(["tablePresentation", k], v, live);

  // columns in display order: listed ones first, then the rest in query order
  const shown = [...order.filter((c) => cols.includes(c)), ...cols.filter((c) => !order.includes(c))];
  const move = (c: string, dir: -1 | 1) => {
    const i = shown.indexOf(c);
    const j = i + dir;
    if (i < 0 || j < 0 || j >= shown.length) return;
    const next = [...shown];
    [next[i], next[j]] = [next[j], next[i]];
    tset("columnOrder", next);
  };
  const toggleHidden = (c: string) => {
    const next = hidden.includes(c) ? hidden.filter((x) => x !== c) : [...hidden, c];
    tset("hiddenColumns", next.length ? next : undefined);
  };

  const keys = ["autoFit", "stickyHeader", "gridlines", "gridColor", "showRowNumbers", "rowHeight", "hiddenColumns", "columnOrder", "columnNumberFormats", "defaultSort"];
  const customised = keys.filter((k) => tp[k] !== undefined).length;

  return (
    <Section id="tbllayout" title={isTable ? "Table layout" : "Layout"} defaultOpen={false} summary={customised ? `${customised} set` : "Default"} canReset={customised > 0} onReset={() => setMany(keys.map((k) => [["tablePresentation", k], undefined] as [string[], any]))}>
      <div className="st-frow-pair" style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 6 }}>
        <button type="button" className={`st-arr__seg${fit === "content" ? " is-on" : ""}`} onClick={() => tset("autoFit", "content")} title="Size every column to its content so the numbers are never cut off">Fit columns to content</button>
        <button type="button" className="st-arr__seg" onClick={() => tset("autoFit", undefined)} disabled={fit === "fill"}>Fill card</button>
      </div>
      <SegRow label="Column sizing" value={fit} onChange={(v) => tset("autoFit", v === "fill" ? undefined : v)} options={[{ value: "fill", label: "Fill card" }, { value: "content", label: "To content" }, { value: "equal", label: "Equal" }]} />
      <ToggleRow label="Sticky header" hint="The header stays visible while the rows scroll." checked={!!tp.stickyHeader} onChange={(v) => tset("stickyHeader", v || undefined)} />
      <SegRow label="Gridlines" value={(tp.gridlines ?? "default") as string} onChange={(v) => tset("gridlines", v === "default" ? undefined : v)} options={[{ value: "default", label: "Look" }, { value: "none", label: "None" }, { value: "horizontal", label: "Rows" }, { value: "vertical", label: "Cols" }, { value: "both", label: "Both" }]} />
      {tp.gridlines && tp.gridlines !== "none" ? <ColorRow label="Gridline colour" value={tp.gridColor} onChange={(v) => tset("gridColor", v, true)} /> : null}
      {isTable ? (
        <>
          <ToggleRow label="Row numbers" checked={!!tp.showRowNumbers} onChange={(v) => tset("showRowNumbers", v || undefined)} />
          <SliderRow label="Row height" hint="Fixed height per row." value={tp.rowHeight} defaultValue={32} min={16} max={160} unit="px" onChange={(v) => tset("rowHeight", v, true)} />
          {cols.length ? (
            <>
              <div className="st-fsub">Sort rows by</div>
              <Row label="Column">
                <Select inputSize="sm" value={tp.defaultSort?.column ?? ""} onChange={(e) => tset("defaultSort", e.target.value ? { column: e.target.value, direction: tp.defaultSort?.direction ?? "asc" } : undefined)}>
                  <option value="">Query order</option>
                  {cols.map((c) => <option key={c} value={c}>{c}</option>)}
                </Select>
              </Row>
              {tp.defaultSort?.column ? (
                <SegRow label="Direction" value={(tp.defaultSort.direction ?? "asc") as "asc" | "desc"} onChange={(v) => tset("defaultSort", { ...tp.defaultSort, direction: v })} options={[{ value: "asc", label: "Ascending" }, { value: "desc", label: "Descending" }]} />
              ) : null}
              <div className="st-fsub">Show, hide and reorder columns</div>
              {shown.map((c, i) => (
                <div key={c} className="st-frow" style={{ alignItems: "center" }}>
                  <div className="st-frow__label" style={{ opacity: hidden.includes(c) ? 0.45 : 1 }}>{c}</div>
                  <div className="st-frow__ctl" style={{ display: "flex", gap: 4, justifyContent: "flex-end" }}>
                    <button type="button" className="st-mini" aria-label={hidden.includes(c) ? `Show ${c}` : `Hide ${c}`} title={hidden.includes(c) ? "Show" : "Hide"} onClick={() => toggleHidden(c)}>{hidden.includes(c) ? <EyeOff size={12} /> : <Eye size={12} />}</button>
                    <button type="button" className="st-mini" aria-label={`Move ${c} up`} disabled={i === 0} onClick={() => move(c, -1)}><ArrowUp size={12} /></button>
                    <button type="button" className="st-mini" aria-label={`Move ${c} down`} disabled={i === shown.length - 1} onClick={() => move(c, 1)}><ArrowDown size={12} /></button>
                  </div>
                </div>
              ))}
              <div className="st-fsub">Number format per column</div>
              {cols.map((c) => (
                <Row key={c} label={c}>
                  <Select inputSize="sm" value={presetOf(formats[c])} onChange={(e) => {
                    const p = FORMAT_PRESETS.find((x) => x.value === e.target.value);
                    if (!p) return;
                    const next = { ...formats };
                    if (p.nf) next[c] = p.nf;
                    else delete next[c];
                    tset("columnNumberFormats", Object.keys(next).length ? next : undefined);
                  }}>
                    {presetOf(formats[c]) === "custom" ? <option value="custom">Custom</option> : null}
                    {FORMAT_PRESETS.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
                  </Select>
                </Row>
              ))}
            </>
          ) : null}
        </>
      ) : null}
    </Section>
  );
}
